import React, { useState, useEffect } from 'react';
import { ArrowLeft, ExternalLink, Loader2, X } from 'lucide-react';
import { getApiBase } from '../lib/apiBase';

const categories = ['All', 'E-Commerce', 'Digitization', 'POS', 'Web Design', 'Tourism', 'Accounting', 'Real Estate', 'Church', 'Donation', 'Other'];

const getInitials = (name) => name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
const DESC_LIMIT = 140;

const PortfolioPage = () => {
  const [active, setActive] = useState('All');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readMore, setReadMore] = useState(null);

  const API = getApiBase();

  useEffect(() => {
    fetch(`${API}/api/portfolio/projects`)
      .then(r => r.json())
      .then(data => {
        setProjects(data.projects || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [API]);

  const filtered = active === 'All' ? projects : projects.filter(p => p.category === active);

  return (
    <div className="min-h-screen bg-white" data-testid="portfolio-page">
      <header className="bg-black py-4 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"><ArrowLeft className="w-4 h-4" /><span className="tracking-wide">Back to Home</span></a>
          <img src="/logos/sag-logo.png" alt="SAG" className="h-8 w-auto" />
        </div>
      </header>

      <section className="py-16 md:py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase block mb-4">Our Work</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-4">Portfolio</h1>
            <p className="text-gray-500 text-base max-w-2xl mx-auto">Delivering elegant digital solutions to businesses across the Caribbean and beyond.</p>
            <div className="w-16 h-[2px] bg-[#C9A227] mx-auto mt-6" />
          </div>

          {/* Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12" data-testid="portfolio-filters">
            {categories.map(c => (
              <button key={c} onClick={() => setActive(c)} className={`px-4 py-2 text-xs tracking-wider uppercase transition-all ${active === c ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20">
              <Loader2 className="w-6 h-6 text-[#C9A227] animate-spin mx-auto mb-3" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400">No projects found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
              {filtered.map((p, i) => {
                const hasValidLink = p.link && /^https?:\/\//i.test(p.link) && !/sebasalliance\.com\/works\//i.test(p.link);
                const isLongDesc = p.description && p.description.length > DESC_LIMIT;
                const shortDesc = isLongDesc ? p.description.slice(0, DESC_LIMIT).trim() + '…' : p.description;

                return (
                  <div
                    key={p.id || i}
                    className="group border border-gray-200 hover:border-[#C9A227]/50 overflow-hidden transition-all duration-300 hover:shadow-[0_16px_40px_-16px_rgba(10,22,40,0.18)] hover:-translate-y-1 flex flex-col bg-white"
                    data-testid={`project-${i}`}
                  >
                    {/* Image */}
                    <div className="aspect-video overflow-hidden flex-shrink-0" style={{ backgroundColor: p.color || '#1e3a5f' }}>
                      {p.image ? (
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-white/90 text-3xl md:text-4xl font-light tracking-wider">{getInitials(p.name)}</span>
                        </div>
                      )}
                    </div>

                    {/* Body — uniform height */}
                    <div className="p-5 bg-white flex flex-col flex-1">
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[#C9A227] font-medium">{p.category}</span>
                      <h3 className="text-base font-medium text-black mt-1 mb-2 group-hover:text-[#1e3a5f] transition-colors flex items-center gap-2">
                        {p.name}
                        {hasValidLink && <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
                      </h3>

                      {/* Description — clamped, with Read More */}
                      {p.description && (
                        <p className="text-gray-500 text-xs leading-relaxed mb-3 flex-1">
                          {shortDesc}
                        </p>
                      )}

                      {/* Footer actions */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto">
                        {isLongDesc ? (
                          <button
                            onClick={() => setReadMore(p)}
                            className="text-[#C9A227] hover:text-[#0a0a0a] text-[10px] tracking-[0.15em] uppercase font-bold transition-colors"
                            data-testid={`read-more-${i}`}
                          >
                            Read More →
                          </button>
                        ) : (
                          <span className="text-gray-300 text-[10px] tracking-[0.15em] uppercase">—</span>
                        )}
                        {hasValidLink && (
                          <a
                            href={p.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#1e3a5f] hover:text-[#C9A227] text-[10px] tracking-[0.15em] uppercase font-bold transition-colors"
                          >
                            Visit
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Stats */}
          <div className="mt-16 text-center">
            <div className="flex flex-wrap justify-center gap-12">
              {[
                { value: `${projects.length}+`, label: 'Projects Completed' },
                { value: '8+', label: 'Years Experience' },
                { value: '100%', label: 'Caribbean Focused' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-light text-[#1e3a5f]">{s.value}</div>
                  <div className="text-gray-400 text-xs uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-black py-6 px-4 text-center">
        <p className="text-white/40 text-xs tracking-wider">Sebastian Alliance Group &bull; Portfolio</p>
      </footer>

      {/* Read-More Modal */}
      {readMore && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setReadMore(null)}
          data-testid="read-more-modal"
        >
          <div
            className="bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header image */}
            {readMore.image && (
              <div className="aspect-video overflow-hidden" style={{ backgroundColor: readMore.color || '#1e3a5f' }}>
                <img src={readMore.image} alt={readMore.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-6 md:p-8 relative">
              <button
                onClick={() => setReadMore(null)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-black hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] font-medium">{readMore.category}</span>
              <h3 className="text-2xl md:text-3xl font-light text-black mt-2 mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {readMore.name}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                {readMore.description}
              </p>
              {readMore.link && /^https?:\/\//i.test(readMore.link) && !/sebasalliance\.com\/works\//i.test(readMore.link) && (
                <a
                  href={readMore.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-6 px-5 py-3 bg-[#0a0a0a] text-white hover:bg-[#C9A227] hover:text-[#0a0a0a] text-xs tracking-[0.2em] uppercase font-bold transition-colors"
                >
                  Visit Project
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioPage;
